import { useState, useEffect, useRef } from "react";

interface StopwatchState {
  elapsedTime: number;
  start: () => void;
  pause: () => void;
  reset: () => void;
  isPaused: boolean;
}

const initTimer: {
  lastSavedElapsedTime: number;
  elapsedTime: number;
  intervalId: NodeJS.Timeout | null;
  start: number;
  manuallyPaused: boolean;
} = {
  lastSavedElapsedTime: 0,
  elapsedTime: 0,
  intervalId: null,
  start: 0,
  manuallyPaused: false,
};

const usePersistantStopwatch = (): StopwatchState => {
  const timer = useRef(initTimer);
  const cu = timer.current;

  const getValueFromLocalStorage = () => {
    let v = parseInt(localStorage.getItem("Persistant_timer") || "0");
    if (isNaN(v) || v < 0) v = 0;
    cu.lastSavedElapsedTime = v;
    cu.elapsedTime = 0;
    cu.start = new Date().getTime();
  };

  const [elapsedTime, setElapsedTime] = useState(cu.lastSavedElapsedTime);
  const [isPaused, setIsPaused] = useState(cu.manuallyPaused);

  const start = () => {
    if (cu.manuallyPaused) {
      cu.manuallyPaused = false;
      setIsPaused(cu.manuallyPaused);
      localStorage.setItem("Persistant_timer_isPaused", "false");
    }
    if (!cu.intervalId) {
      getValueFromLocalStorage();
      cu.intervalId = setInterval(() => {
        cu.elapsedTime =
          new Date().getTime() - cu.start + cu.lastSavedElapsedTime;

        if (!(cu.elapsedTime % 1)) setElapsedTime(cu.elapsedTime);

        localStorage.setItem("Persistant_timer", cu.elapsedTime.toString());
      }, 10);
    }
  };

  const pause = () => {
    cu.manuallyPaused = true;
    setIsPaused(cu.manuallyPaused);
    localStorage.setItem("Persistant_timer_isPaused", "true");
    cu.lastSavedElapsedTime = cu.elapsedTime;
    if (cu.intervalId) {
      clearInterval(cu.intervalId);
      cu.intervalId = null;
    }
  };

  const reset = () => {
    pause();
    cu.lastSavedElapsedTime = 0;
    cu.elapsedTime = 0;
    localStorage.setItem("Persistant_timer", "0");
    cu.start = new Date().getTime();
    setElapsedTime(0);
  };

  useEffect(() => {
    getValueFromLocalStorage();
    cu.elapsedTime = new Date().getTime() - cu.start + cu.lastSavedElapsedTime;
    if (!(cu.elapsedTime % 1)) setElapsedTime(cu.elapsedTime);
    let Persistant_timer_pausedStatus = localStorage.getItem(
      "Persistant_timer_isPaused"
    );
    //If the timer isPaused varaiable is not initialized locally yet, pause the timer
    if (Persistant_timer_pausedStatus === null)
      Persistant_timer_pausedStatus = "true";

    let istimerPaused = Persistant_timer_pausedStatus === "true" || false;

    if (istimerPaused === true) {
      cu.manuallyPaused = true;
      setIsPaused(cu.manuallyPaused);
    }

    window.onfocus = () => {
      if (cu.manuallyPaused) return;
    };

    if (cu.manuallyPaused) {
      return;
    }

    start();

    return () => {
      if (cu.intervalId) {
        clearInterval(cu.intervalId);
        cu.intervalId = null;
      }
    };
  }, []);

  return { elapsedTime, start, pause, reset, isPaused };
};

export default usePersistantStopwatch;
